import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  FileText, Upload, Trash2, Loader2, ChevronRight, Users, Music,
  Sparkles, Search, User, ArrowRight,
} from "lucide-react";
import { Input } from "@/components/ui/input";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

export default function Script() {
  const { user, production } = useAuth();
  const nav = useNavigate();
  const [script, setScript] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("scenes");
  const [q, setQ] = useState("");
  const fileRef = useRef(null);
  const isEditor = user?.role === "org_admin" || user?.role === "production_staff" || user?.role === "super_admin";

  const load = async () => {
    if (!production?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get("/scripts");
      setScript(data?.empty ? null : data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load script");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (production?.id) load(); }, [production?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/scripts/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setScript(data);
      toast.success(`Analyzed ${data.stats.scenes} scenes, ${data.stats.characters} characters, ${data.stats.songs} songs`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analysis failed");
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeScript = async () => {
    if (!confirm("Remove the current script analysis? You can re-upload anytime.")) return;
    try {
      await api.delete("/scripts");
      setScript(null);
      toast.success("Script analysis cleared");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const filteredCharacters = useMemo(() => {
    const list = script?.characters || [];
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter((c) => c.name.toLowerCase().includes(s));
  }, [script, q]);

  const openCharacter = (name) => {
    // Attempt to find the character record and navigate to its detail page.
    api.get("/characters").then(({ data }) => {
      const hit = (data || []).find((c) => c.name.toLowerCase() === name.toLowerCase());
      if (hit) nav(`/characters/${hit.id}`);
      else toast.info(`No matching character record for "${name}" yet. Add ${name} to Characters to enable the deep-view.`);
    }).catch(() => {});
  };

  return (
    <div className="space-y-6" data-testid="script-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Script Intelligence</div>
          <h1 className="mt-1 font-display text-4xl md:text-5xl tracking-tight">Script</h1>
          <p className="mt-2 text-sm text-zinc-400 max-w-2xl">
            Upload a PDF of your script and we&apos;ll detect acts, scenes, songs, characters, line counts,
            entrances / exits, props, costume references, and lighting / sound / video cues automatically.
          </p>
        </div>
        {isEditor && (
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept=".pdf,application/pdf" className="hidden"
                   onChange={(e) => upload(e.target.files?.[0])} data-testid="script-file-input" />
            <button data-testid="script-upload-btn" onClick={() => fileRef.current?.click()} disabled={busy}
                    style={goldBtn} className="h-10 px-4 rounded font-semibold text-sm inline-flex items-center gap-1.5 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {script ? "Re-analyze PDF" : "Upload script"}
            </button>
            {script && (
              <button onClick={removeScript} className="h-10 w-10 grid place-items-center rounded border border-red-500/30 text-red-400 hover:bg-red-500/10" title="Remove analysis">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </header>

      {loading && <div className="text-zinc-500 text-sm">Loading…</div>}

      {!loading && !script && (
        <div className="rounded-2xl border border-dashed border-white/15 bg-[#0F0F0F] p-10 text-center" data-testid="script-empty">
          <FileText className="w-8 h-8 text-gold mx-auto" />
          <h2 className="mt-3 font-display text-2xl">No script analyzed yet</h2>
          <p className="mt-2 text-sm text-zinc-400 max-w-md mx-auto">
            Upload a PDF above. Musical theatre scripts have varied formatting — expect ~70-90% accuracy on
            props / costumes / cues. Enhanced LLM analysis is coming as an opt-in.
          </p>
        </div>
      )}

      {!loading && script && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Stat label="Acts"       n={script.stats.acts} />
            <Stat label="Scenes"     n={script.stats.scenes} />
            <Stat label="Songs"      n={script.stats.songs} />
            <Stat label="Characters" n={script.stats.characters} />
            <Stat label="Lines"      n={script.stats.total_lines} tint="text-gold" />
          </div>

          <div className="flex items-center gap-2 border-b border-white/10">
            {[["scenes","Scenes"], ["characters","Characters"], ["songs","Songs"]].map(([id, label]) => (
              <button key={id} onClick={() => setTab(id)} data-testid={`script-tab-${id}`}
                className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${tab === id ? "border-gold text-white" : "border-transparent text-zinc-400 hover:text-white"}`}>
                {label}
              </button>
            ))}
          </div>

          {tab === "scenes" && (
            <div className="space-y-4">
              {(script.acts || []).map((act) => (
                <div key={`act-${act.number}`} className="rounded-2xl border border-white/10 bg-[#0F0F0F]">
                  <div className="p-4 border-b border-white/5 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-gold" />
                    <div className="font-display text-xl">Act {act.number}{act.title ? ` · ${act.title}` : ""}</div>
                    <span className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500">{act.scenes.length} scenes</span>
                  </div>
                  <ul className="divide-y divide-white/5">
                    {act.scenes.map((sc, i) => (
                      <li key={i} className="p-4">
                        <div className="flex items-center gap-2">
                          <div className="font-medium text-zinc-100">Scene {sc.number}{sc.title ? ` — ${sc.title}` : ""}</div>
                          <span className="ml-auto text-[11px] text-zinc-500">{sc.line_count} lines · {sc.speakers.length} speakers</span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {sc.speakers.map((s, idx) => (
                            <button key={idx} onClick={() => openCharacter(s)}
                              className="text-[11px] px-2 py-0.5 rounded bg-white/5 text-zinc-300 hover:bg-gold/10 hover:text-gold">
                              {s}
                            </button>
                          ))}
                        </div>
                        {sc.songs.length > 0 && (
                          <div className="mt-2 text-[11px] text-zinc-400">
                            <span className="text-gold">♪</span> {sc.songs.join(" · ")}
                          </div>
                        )}
                        <div className="mt-2 grid grid-cols-2 lg:grid-cols-4 gap-1.5 text-[11px]">
                          <SdChip label="Entrances" items={sc.entrances} />
                          <SdChip label="Exits" items={sc.exits} />
                          <SdChip label="Props" items={sc.props} />
                          <SdChip label="Costumes" items={sc.costume_refs} />
                        </div>
                        {(sc.light_cues.length + sc.sound_cues.length + sc.video_cues.length > 0) && (
                          <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-1.5 text-[11px]">
                            <SdChip label="Lighting" items={sc.light_cues} accent="text-amber-300" />
                            <SdChip label="Sound"    items={sc.sound_cues} accent="text-cyan-300" />
                            <SdChip label="Video"    items={sc.video_cues} accent="text-fuchsia-300" />
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {tab === "characters" && (
            <div>
              <div className="relative mb-4">
                <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search characters…"
                  className="pl-9 bg-[#121212] border-white/10 h-11" data-testid="script-char-search" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {filteredCharacters.map((c) => (
                  <button key={c.name} onClick={() => openCharacter(c.name)}
                    className="text-left rounded-lg border border-white/10 bg-[#0F0F0F] p-4 hover:border-gold/40 hover:bg-gold/5 transition-colors group">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gold" />
                      <div className="font-medium truncate">{c.name}</div>
                      <ArrowRight className="w-3.5 h-3.5 ml-auto text-zinc-600 group-hover:text-gold" />
                    </div>
                    <div className="mt-1 text-[11px] text-zinc-500">{c.lines_count} lines · appears in {c.scenes.length} scenes</div>
                    {c.sample_line && <div className="mt-2 text-[11px] text-zinc-500 italic line-clamp-2">&ldquo;{c.sample_line}&rdquo;</div>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {tab === "songs" && (
            <ul className="divide-y divide-white/5 rounded-2xl border border-white/10 bg-[#0F0F0F]">
              {(script.songs || []).length === 0 && <li className="p-6 text-center text-zinc-500 text-sm">No songs detected.</li>}
              {script.songs.map((s, i) => (
                <li key={i} className="p-3 flex items-center gap-3">
                  <Music className="w-4 h-4 text-gold" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-100 truncate">{s.title}</div>
                    <div className="text-[11px] text-zinc-500">Act {s.act} · Scene {s.scene}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, n, tint }) {
  return (
    <div className="rounded-lg border border-white/10 bg-[#0F0F0F] p-4">
      <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">{label}</div>
      <div className={`mt-1 text-3xl font-display ${tint || "text-zinc-100"}`}>{n}</div>
    </div>
  );
}
function SdChip({ label, items, accent }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="rounded-md border border-white/10 bg-[#121212] p-2">
      <div className={`text-[9px] uppercase tracking-widest ${accent || "text-zinc-500"}`}>{label} · {items.length}</div>
      <div className="mt-1 text-[11px] text-zinc-400 line-clamp-2">{items.slice(0, 2).join(" · ")}</div>
    </div>
  );
}
